import React from 'react';
import { Insight } from '@/api/insightsApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { chartColors } from './chartColors';
import { CustomTooltip } from './CustomTooltip';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useRechartsTheme } from '@/hooks/useRechartsTheme';

interface ResourceUnderutilizationVisualizationProps {
  insight: Insight;
}

export const ResourceUnderutilizationVisualization: React.FC<ResourceUnderutilizationVisualizationProps> = ({ insight }) => {
  const { theme } = useAppTheme();
  const chartTheme = useRechartsTheme();
  // Parse description to extract utilization data
  const desc = insight.description || '';
  const utilMatch = desc.match(/([\d.]+)%/);
  const utilization = utilMatch ? Number.parseFloat(utilMatch[1]) : 40;
  const assigned = Math.floor(100 * (utilization / 100));
  const idle = 100 - assigned;

  const utilizationData = [
    { category: 'Assigned', value: assigned, color: chartColors.primary },
    { category: 'Idle', value: idle, color: chartColors.gray },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Utilization Rate</div>
          <div className="text-2xl font-bold text-amber-400">{utilization.toFixed(1)}%</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Assigned Resources</div>
          <div className="text-2xl font-bold text-nb-accent-blue">{assigned}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Idle Resources</div>
          <div className={`text-2xl font-bold ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>{idle}</div>
        </div>
      </div>

      {/* Utilization Chart */}
      <div className={`rounded-lg p-4 border ${
        theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h4 className={`text-sm font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Resource Utilization</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={utilizationData}
            margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
            <XAxis 
              dataKey="category" 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
            />
            <YAxis 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
              label={{ value: 'Resources', angle: -90, position: 'insideLeft', style: { fill: chartTheme.textSecondary } }}
            />
            <Tooltip 
              content={<CustomTooltip formatter={(value: number) => [`${value} resources`, 'Count']} />}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {utilizationData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};



